import {
  getPromptModelsQuery,
  ServerDeploymentType,
  SubscriptionStatus,
} from '@affine/graphql';
import {
  createSignalFromObservable,
  type Signal,
} from '@blocksuite/affine/shared/utils';
import { signal } from '@preact/signals-core';
import { LiveData, Service } from '@toeverything/infra';

import type {
  AuthService,
  GraphQLService,
  ServerService,
  SubscriptionService,
} from '../../cloud';
import type { GlobalStateService } from '../../storage';

const AI_MODEL_ID_KEY = 'AIModelId';

export interface AIModel {
  name: string;
  id: string;
  version: string;
  category: string;
  isPro: boolean;
  isDefault: boolean;
}

export function mapAIModels(
  defaultModel: string,
  optionalModels: Array<{ id: string; name: string }>,
  proModels: Array<{ id: string }>
): AIModel[] {
  return optionalModels.map(model => {
    const [category] = model.name.split(' ');
    const version = model.name.slice(category.length + 1);
    return {
      name: model.name,
      id: model.id,
      version,
      category,
      isPro: proModels.some(proModel => proModel.id === model.id),
      isDefault: model.id === defaultModel,
    };
  });
}

export function resolveAIModelSelection(
  selectedModelId: string | undefined,
  models: AIModel[]
): string | undefined {
  if (models.some(model => model.id === selectedModelId)) {
    return selectedModelId;
  }

  return models.find(model => model.isDefault)?.id;
}

export function canSelectAIModel(
  model: AIModel | undefined,
  isSubscribed: boolean,
  isSelfHosted: boolean
) {
  if (!model) return false;
  return isSelfHosted || !model.isPro || isSubscribed;
}

export class AIModelService extends Service {
  modelId: Signal<string | undefined>;

  models: Signal<AIModel[]> = signal([]);

  private modelCatalogRequest = 0;

  private readonly modelId$ = LiveData.from(
    this.globalStateService.globalState.watch<string>(AI_MODEL_ID_KEY),
    undefined
  );

  constructor(
    private readonly globalStateService: GlobalStateService,
    private readonly gqlService: GraphQLService,
    private readonly subscriptionService: SubscriptionService,
    private readonly authService: AuthService,
    private readonly serverService: ServerService
  ) {
    super();

    const { signal: modelId, cleanup } = createSignalFromObservable<
      string | undefined
    >(this.modelId$, undefined);
    this.modelId = modelId;
    this.disposables.push(cleanup);

    this.init().catch(err => {
      console.error(err);
    });
  }

  resetModel = () => {
    this.globalStateService.globalState.set(AI_MODEL_ID_KEY, undefined);
  };

  setModel = (modelId: string) => {
    const isSubscribed =
      this.subscriptionService.subscription.ai$.value?.status ===
      SubscriptionStatus.Active;
    const model = this.models.value.find(model => model.id === modelId);
    if (!model) {
      this.resetModel();
      return;
    }
    const isSelfHosted =
      this.serverService.server.config$.value?.type ===
      ServerDeploymentType.Selfhosted;
    if (!canSelectAIModel(model, isSubscribed, isSelfHosted)) {
      return;
    }
    this.globalStateService.globalState.set(AI_MODEL_ID_KEY, modelId);
  };

  private readonly init = async () => {
    const accountSub = this.authService.session.account$.subscribe(account => {
      if (!account) {
        this.models.value = [];
        return;
      }
      this.initModels().catch(err => {
        console.error(err);
      });
    });
    this.disposables.push(() => accountSub.unsubscribe());

    // subscribe to ai purchase status
    const sub = this.subscriptionService.subscription.ai$.subscribe(
      subscription => {
        const isSubscribed = subscription?.status === SubscriptionStatus.Active;
        const model = this.models.value.find(
          model => model.id === this.modelId.value
        );
        const isSelfHosted =
          this.serverService.server.config$.value?.type ===
          ServerDeploymentType.Selfhosted;
        if (model && !canSelectAIModel(model, isSubscribed, isSelfHosted)) {
          this.resetModel();
        }
      }
    );
    this.disposables.push(() => sub.unsubscribe());
  };

  private readonly initModels = async (prompt?: string) => {
    const request = ++this.modelCatalogRequest;
    const promptName = prompt || 'Chat With AFFiNE AI';
    const catalog = await this.getModelsByPrompt(promptName);
    if (request !== this.modelCatalogRequest) return;

    const mappedModels = catalog
      ? mapAIModels(
          catalog.defaultModel,
          catalog.optionalModels,
          catalog.proModels
        )
      : [];
    const selectedModelId = resolveAIModelSelection(
      this.modelId.value,
      mappedModels
    );

    this.models.value = mappedModels;
    if (selectedModelId !== this.modelId.value) {
      this.globalStateService.globalState.set(AI_MODEL_ID_KEY, selectedModelId);
    }
  };

  private readonly getModelsByPrompt = async (promptName: string) => {
    return this.gqlService
      .gql({
        query: getPromptModelsQuery,
        variables: { promptName },
      })
      .then(res => res.currentUser?.copilot?.models);
  };
}
